import React, { useCallback, useMemo } from "react";

import TeamItemView from "./TeamItemView";

import {
  RosterManagementListMyOrganizationsPlayersDocument,
  RosterManagementTeamFragment,
  useRosterManagementCreateOrganizationTeamMutation,
  useRosterManagementDeleteOrganizationTeamMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

/**
 *
 */
interface Props {
  item: RosterManagementTeamFragment;
  organizationId: string;
}

const refetchQueries = [
  { query: RosterManagementListMyOrganizationsPlayersDocument },
];

/**
 * Container for a team list item with add/remove functionality.
 * @param root0 - Component props
 * @param root0.item - Team data including id, name, and selection status
 * @param root0.organizationId - Current organization ID for team association
 */
const TeamItemContainer = ({ item, organizationId }: Props) => {
  const hasSelectedTeam = useMemo(
    () => item.isMyOrganizationTeam,
    [item.isMyOrganizationTeam]
  );
  const recordCreateOrganizationTeamGoal = useRecordUserGoal({
    goalType: GoalType.CreateOrganizationTeam,
    value: item.name,
  });
  const recordDeleteOrganizationTeamGoal = useRecordUserGoal({
    goalType: GoalType.DeleteOrganizationTeam,
    value: item.name,
  });
  const [addTeam] = useRosterManagementCreateOrganizationTeamMutation({
    variables: {
      input: {
        organizationId,
        teamId: item.id,
      },
    },
    refetchQueries,
    notifyOnNetworkStatusChange: true,
    onCompleted: data =>
      recordCreateOrganizationTeamGoal({
        value: data?.createOrganizationTeam?.teamId,
        goalType: GoalType.CreateOrganizationTeam,
      }),
    update: (cache, { data, errors }) => {
      const teamId = data?.createOrganizationTeam.teamId;
      if (!!teamId && !errors) {
        cache.modify({
          id: cache.identify({ __typename: "Team", id: teamId }),
          fields: {
            isMyOrganizationTeam: () => true,
          },
        });
      }
    },
    optimisticResponse: {
      __typename: "Mutation",
      createOrganizationTeam: {
        __typename: "OrganizationTeam",
        teamId: item.id,
        id: `temp-id-${new Date().toISOString()}`,
        organizationId,
      },
    },
  });

  const [removeTeam] = useRosterManagementDeleteOrganizationTeamMutation({
    variables: {
      input: {
        organizationId,
        teamId: item.id,
      },
    },
    refetchQueries,
    notifyOnNetworkStatusChange: true,
    onCompleted: data =>
      recordDeleteOrganizationTeamGoal({
        value: data?.deleteOrganizationTeam?.teamId,
        goalType: GoalType.DeleteOrganizationTeam,
      }),
    update: (cache, { data, errors }) => {
      const teamId = data?.deleteOrganizationTeam.teamId;
      if (!!teamId && !errors) {
        cache.modify({
          id: cache.identify({ __typename: "Team", id: teamId }),
          fields: {
            isMyOrganizationTeam: () => false,
          },
        });
      }
    },
    optimisticResponse: {
      __typename: "Mutation",
      deleteOrganizationTeam: {
        __typename: "OrganizationTeam",
        teamId: item.id,
        id: item.id,
        organizationId,
      },
    },
  });

  const handleChange = useCallback(() => {
    if (!hasSelectedTeam) {
      addTeam();
    } else {
      removeTeam();
    }
  }, [addTeam, hasSelectedTeam, removeTeam]);

  return (
    <TeamItemView
      item={item}
      hasSelectedTeam={!!hasSelectedTeam}
      onChange={handleChange}
    />
  );
};

export default TeamItemContainer;
